export default {
  methods: {
    /**
     * Get error message for a field.
     * @param {string} field - Field path (e.g., 'vehicle.licensePlate')
     * @param {object} messages - Validator-key → human message map
     * @returns {Array}
     *
     * Errors are shown when:
     *   - the user has touched the field ($dirty), OR
     *   - the field was pre-filled with a value that fails validation.
     *
     * Empty fields that haven't been touched never show a "required" error,
     * so there is no need to touch/dirty fields artificially on mount or
     * after programmatic data fills (e.g. from a popup).
     */
    getValidationErrors(field, messages = {}) {
      if (!this.$v) return [];
      const fieldPath = field.split('.');
      let validation = this.$v;

      for (const path of fieldPath) {
        validation = validation[path];
        if (!validation) return [];
      }

      let value = this.$data;
      for (const path of fieldPath) {
        value = value?.[path];
      }

      const hasValue = value != null && value !== '';

      if (!validation.$dirty && !hasValue) return [];

      const errors = [];

      Object.keys(validation.$params).forEach(param => {
        if (!validation[param]) {
          errors.push(messages[param] || `Помилка валідації: ${param}`);
        }
      });

      return errors;
    },

    /**
     * Check if field has a visible error.
     * Mirrors getValidationErrors: dirty OR pre-filled-invalid.
     * @param {string} field - Field path
     * @returns {boolean}
     */
    hasError(field) {
      if (!this.$v) return false;
      const fieldPath = field.split('.');
      let validation = this.$v;

      for (const path of fieldPath) {
        validation = validation[path];
        if (!validation) return false;
      }

      let value = this.$data;
      for (const path of fieldPath) {
        value = value?.[path];
      }

      const hasValue = value != null && value !== '';

      return (validation.$dirty || hasValue) && validation.$invalid;
    },

    /**
     * Touch field (mark as dirty).
     * @param {string} field - Field path
     */
    touchField(field) {
      if (!this.$v) return;
      const fieldPath = field.split('.');
      let validation = this.$v;

      for (const path of fieldPath) {
        validation = validation[path];
        if (!validation) return;
      }

      validation.$touch();
    },

    /**
     * Reset field validation.
     * @param {string} field - Field path
     */
    resetFieldValidation(field) {
      if (!this.$v) return;
      const fieldPath = field.split('.');
      let validation = this.$v;

      for (const path of fieldPath) {
        validation = validation[path];
        if (!validation) return;
      }

      validation.$reset();
    },

    /**
     * Validate this component and all declared child forms.
     * Declare child refs via `validationChildren: ['refName']` in component options.
     * @returns {boolean}
     */
    validateAll() {
      if (this.$v) this.$v.$touch();
      const selfValid = this.$v ? !this.$v.$invalid : true;
      const childRefs = this.$options.validationChildren || [];
      const childResults = childRefs
        .map(refName => this.$refs[refName])
        .filter(Boolean)
        .map(child => child.validateAll?.() ?? true);
      return selfValid && childResults.every(Boolean);
    },

    /**
     * Reset all validations.
     */
    resetAllValidations() {
      this.$v.$reset();
    },
  },
};
